// Every string AttentionItem can render of its own, and the shape a caller may
// replace them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// The row's only words of its own are the eyebrow over its title, one per tone.
// Everything else it draws — the title, the reason, the deadline and the
// action's label — arrives from the page already in the reader's language.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

/**
 * The English defaults, frozen, keyed by tone.
 *
 * The first three are the words the Dashboard screen prints over its Needs-you
 * rows. The handoff draws no `info` row, so `info` names its tone and nothing
 * more. Upper case is the row's CSS, not the strings'.
 */
export const ATTENTION_ITEM_TRANSLATIONS = Object.freeze({
  live: 'Live',
  soon: 'Waiting on you',
  money: 'Not yet paid',
  info: 'Info',
});

/**
 * The keys in `translations` that AttentionItem would not read, as dotted paths.
 *
 * The walk itself is BidCard's, which takes the defaults object as an argument,
 * so this surface passes its own rather than owning a second copy of a
 * comparison that has to behave identically everywhere.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, ATTENTION_ITEM_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) return ATTENTION_ITEM_TRANSLATIONS;

  return { ...ATTENTION_ITEM_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidAttentionItemI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations')) return false;
  if (i18n.translations === undefined) return true;
  return unreadTranslationKeys(i18n.translations).length === 0;
}
